/**
 * Catálogo de reportes de gestión para el dueño de clínicas.
 * Cada bloque del viejo dashboard vive acá como pantalla dedicada bajo `/reports/...`
 * (URLs legacy / deep links; el menú principal ya no lista este catálogo).
 *
 * Regla: el detalle se migra por dominio (Finanzas, Pacientes…), no como hub lateral.
 * Ver `docs/NAV_REPORTES_POR_DOMINIO.md` y checklist cierre US-002 #60.
 *
 * US-003: una entrada por concepto (23; antes 26 con alias en catálogo).
 * Alias solo como URL (`REPORT_LEGACY_REDIRECTS`): payment-methods, recurrence,
 * coofit-*, billing-vs-revenue, data-quality, audit.
 *
 * - `page` = pantalla dedicada (única que puede listarse en nav/catálogo residual)
 * - `download` = export PDF/XLSX; no se anuncia en menú. Superficie = CTA real
 *   (`clinic_summary` → `/clinics/{id}` #62; `directory_pack` → Dashboard/Finanzas)
 * - `soon` = roadmap (sin API / sin página). No listar. URL directa → ComingSoon.
 */
export type ReportAvailability = 'page' | 'download' | 'soon';

/**
 * Dominios de negocio de la biblioteca.
 */
export type ReportCategoryId =
  | 'business'
  | 'clinics'
  | 'professionals'
  | 'appointments'
  | 'patients'
  | 'finances'
  | 'quality'
  | 'platform';

export type ReportExportKind = 'summary' | 'comparison' | 'caja' | 'directory_pack';

/** Casa canónica de la comparativa guiada multi-clínica (no `/clinics?view=compare`). */
export const COMPARISON_REPORT_PATH = '/reports/comparison';

export interface ReportDefinition {
  id: string;
  category: ReportCategoryId;
  availability: ReportAvailability;
  /** Permiso adicional (además de reports.view). */
  permission?: string;
  /** Requiere elegir una clínica concreta. */
  requiresClinic?: boolean;
  /** Requiere más de una clínica en el scope. */
  requiresMultiClinic?: boolean;
  /** Export cableado al API (hub). */
  exportKind?: ReportExportKind;
  /** Ruta de página dedicada bajo Reportes. */
  pagePath?: string;
  /**
   * Si es `false`, no se lista en el hub (alias / duplicado de otro reporte).
   * Sigue existiendo para rutas, permisos y favoritos legacy.
   */
  listInHub?: boolean;
  /** Puede anclarse como widget del dashboard. Desactivado: reportes viven en el hub. */
  widgetable?: boolean;
}

export const REPORT_CATEGORIES: ReportCategoryId[] = [
  'business',
  'clinics',
  'finances',
  'appointments',
  'professionals',
  'patients',
  'quality',
  'platform',
];

export const REPORTS_CATALOG: ReportDefinition[] = [
  // —— Negocio ——
  {
    id: 'main_principal',
    category: 'business',
    availability: 'page',
    permission: 'reports.view',
    requiresClinic: true,
    pagePath: '/reports/main-principal',
  },
  {
    id: 'clinic_economics',
    category: 'business',
    availability: 'page',
    permission: 'reports.view',
    requiresClinic: true,
    pagePath: '/reports/clinic',
  },
  {
    // CTA real: ClinicHubPage (`/clinics/:tenantId`, US-023 #62). No listar en menú.
    id: 'clinic_summary',
    category: 'business',
    availability: 'download',
    permission: 'reports.export',
    requiresClinic: true,
    exportKind: 'summary',
    listInHub: false,
  },
  {
    // CTA en Dashboard (`/`) y Finanzas (`/finances`). Scope = allowedTenantIds.
    id: 'directory_pack',
    category: 'business',
    availability: 'download',
    permission: 'reports.export',
    exportKind: 'directory_pack',
    listInHub: false,
  },
  {
    id: 'annual_executive',
    category: 'business',
    availability: 'page',
    permission: 'reports.export',
    requiresClinic: true,
    pagePath: '/reports/annual',
  },

  // —— Clínicas ——
  {
    // Ver: reports.compare. Export CTA: canExportReports() → reports.export.
    id: 'clinics_comparison',
    category: 'clinics',
    availability: 'page',
    permission: 'reports.compare',
    requiresMultiClinic: true,
    pagePath: COMPARISON_REPORT_PATH,
  },
  {
    id: 'consolidated_board',
    category: 'clinics',
    availability: 'page',
    permission: 'dashboard.view',
    pagePath: '/reports/consolidated',
  },

  // —— Finanzas (US-002 F2: destino = tabs de /finances; no catálogo lateral) ——
  {
    // Canónico de caja / medios de pago. Alias URL: /reports/payment-methods.
    id: 'cashflow',
    category: 'finances',
    availability: 'page',
    permission: 'reports.export',
    requiresClinic: true,
    pagePath: '/finances?tab=methods',
    listInHub: false,
  },
  {
    id: 'plan_ranking',
    category: 'finances',
    availability: 'page',
    permission: 'metrics.ranking_planes',
    requiresClinic: true,
    pagePath: '/finances?tab=plans',
    listInHub: false,
  },
  {
    id: 'revenue_trend',
    category: 'finances',
    availability: 'page',
    permission: 'metrics.tendencia',
    requiresClinic: true,
    pagePath: '/finances?tab=trend',
    listInHub: false,
  },
  {
    // Canónico de Suscripción. Alias URL: /reports/coofit-plan, /reports/coofit-debt.
    id: 'billing_vs_revenue',
    category: 'finances',
    availability: 'page',
    permission: 'metrics.plan_recaudacion',
    requiresClinic: true,
    pagePath: '/subscription',
    listInHub: false,
  },
  {
    id: 'lifetime_value',
    category: 'finances',
    availability: 'page',
    permission: 'metrics.caja',
    requiresClinic: true,
    pagePath: '/reports/lifetime-value',
  },
  {
    // Roadmap: sin API de ganancia por %. No nav; URL directa → ComingSoon (no 404/500).
    id: 'ownership_profit',
    category: 'finances',
    availability: 'soon',
    permission: 'reports.view',
    pagePath: '/reports/ownership-profit',
    listInHub: false,
  },

  // —— Turnos ——
  {
    id: 'appointments',
    category: 'appointments',
    availability: 'page',
    permission: 'metrics.turnos',
    requiresClinic: true,
    pagePath: '/reports/appointments',
  },
  {
    id: 'occupancy',
    category: 'appointments',
    availability: 'page',
    permission: 'metrics.turnos',
    requiresClinic: true,
    pagePath: '/reports/occupancy',
  },
  {
    id: 'cancellations_noshow',
    category: 'appointments',
    availability: 'page',
    permission: 'metrics.turnos',
    requiresClinic: true,
    pagePath: '/reports/cancellations',
  },

  // —— Profesionales (US-002 F4: dominio /professionals; legacy soft-redirect) ——
  {
    id: 'professionals',
    category: 'professionals',
    availability: 'page',
    permission: 'metrics.profesionales',
    requiresClinic: true,
    pagePath: '/professionals',
    listInHub: false,
  },

  // —— Pacientes (US-002 F3: KPIs → tabs de /patients; listas conversion/no-return siguen vivas) ——
  {
    id: 'patients_overview',
    category: 'patients',
    availability: 'page',
    permission: 'metrics.pacientes',
    requiresClinic: true,
    pagePath: '/patients?tab=overview',
    listInHub: false,
  },
  {
    // Canónico de conversión. Alias URL: /reports/recurrence.
    id: 'conversion',
    category: 'patients',
    availability: 'page',
    permission: 'metrics.recurrencia',
    requiresClinic: true,
    pagePath: '/reports/conversion',
    listInHub: false,
  },
  {
    id: 'no_return',
    category: 'patients',
    availability: 'page',
    permission: 'metrics.no_volvieron',
    requiresClinic: true,
    pagePath: '/reports/no-return',
    listInHub: false,
  },
  {
    id: 'new_vs_returning',
    category: 'patients',
    availability: 'page',
    permission: 'metrics.recurrencia',
    requiresClinic: true,
    pagePath: '/patients?tab=acquisition',
    listInHub: false,
  },

  // —— Calidad (dominio Operaciones) ——
  {
    id: 'data_quality',
    category: 'quality',
    availability: 'page',
    permission: 'metrics.calidad',
    requiresClinic: true,
    pagePath: '/operations',
    listInHub: false,
  },
  {
    id: 'audit_trail',
    category: 'quality',
    availability: 'page',
    permission: 'reports.view',
    pagePath: '/operations?tab=audit',
    listInHub: false,
  },
];

export function getReportById(id: string): ReportDefinition | undefined {
  return REPORTS_CATALOG.find((report) => report.id === id);
}

/** Listado activo (hub residual / sidebar legacy). Nunca `soon` ni download-only. */
export function isListedInHub(report: ReportDefinition): boolean {
  if (report.listInHub === false) return false;
  return report.availability === 'page' && Boolean(report.pagePath);
}

export function listActiveCatalog(reports: readonly ReportDefinition[] = REPORTS_CATALOG): ReportDefinition[] {
  return reports.filter(isListedInHub);
}
